/**
 * Alsunu — customer video rail ("Sizden Gelenler").
 *
 * Any number of short vertical clips on a swipeable rail, each with its own
 * play/pause and mute toggle; arrows, a progress bar and a counter on desktop.
 *
 * Deliberate choices:
 * • Nothing autoplays. Three clips starting on their own would cost more
 *   bandwidth than the rest of the product page, and sound arriving unasked on
 *   a product page is hostile. Each `<video>` is preload="none" behind a poster
 *   until the shopper taps it.
 * • Only one plays at a time. Starting a second clip pauses the first, so the
 *   rail never turns into three competing soundtracks.
 * • A clip that scrolls out of view pauses itself, which also stops audio
 *   following the shopper down the page.
 * • Controls respond on the button, not on the video surface, so the swipeable
 *   rail stays swipeable — a tap-anywhere-to-play target would eat every
 *   horizontal drag that starts on a clip.
 */
(() => {
  if (window.__alsUgc) return;
  window.__alsUgc = true;

  const VIDEO = '[data-ugc-video]';

  /** @param {HTMLVideoElement} video */
  const syncPlayState = (video) => {
    const item = video.closest('.als-ugc__item');
    if (item) item.classList.toggle('is-playing', !video.paused);
  };

  /** @param {HTMLVideoElement} video */
  const syncMuteState = (video) => {
    const item = video.closest('.als-ugc__item');
    const button = item?.querySelector('[data-ugc-mute]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(video.muted));
    button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    item.classList.toggle('is-muted', video.muted);
  };

  const pauseOthers = (current) => {
    document.querySelectorAll(VIDEO).forEach((video) => {
      if (video !== current && !video.paused) {
        video.pause();
        syncPlayState(video);
      }
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const playBtn = target.closest('[data-ugc-play]');
      const muteBtn = target.closest('[data-ugc-mute]');
      if (!playBtn && !muteBtn) return;

      const item = (playBtn || muteBtn).closest('.als-ugc__item');
      const video = item?.querySelector(VIDEO);
      if (!video) return;

      if (muteBtn) {
        video.muted = !video.muted;
        syncMuteState(video);
        return;
      }

      if (video.paused) {
        // preload="none" means the first play has to fetch; upgrading the hint
        // here keeps a second play instant.
        if (video.preload === 'none') video.preload = 'auto';
        pauseOthers(video);
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    },
    { passive: true }
  );

  // Keep the button state honest when playback changes for any other reason
  // (ended, stalled, paused by the OS).
  ['play', 'pause'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const video = event.target;
        if (video instanceof HTMLVideoElement && video.matches(VIDEO)) syncPlayState(video);
      },
      true
    );
  });

  const pad = (n) => String(n).padStart(2, '0');
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Rail: any number of clips. Arrows page by however many cards are fully
   * visible, the progress bar mirrors the scroll position, the counter names
   * the clips in view, and edge fades appear only on sides with hidden
   * clips. Controls stay hidden while everything fits.
   * @param {HTMLElement} root
   */
  const setupRail = (root) => {
    const rail = root.querySelector('[data-ugc-rail]');
    // Controls sit right next to the rail wrapper (customer videos and the
    // "See It Up Close" gallery share this markup).
    const controls = root.parentElement?.querySelector(':scope > [data-ugc-controls]');
    if (!rail) return;
    const items = Array.from(rail.querySelectorAll('[data-ugc-item]'));
    const prev = controls?.querySelector('[data-ugc-prev]');
    const next = controls?.querySelector('[data-ugc-next]');
    const bar = controls?.querySelector('[data-ugc-bar]');
    const current = controls?.querySelector('[data-ugc-current]');
    const total = controls?.querySelector('[data-ugc-total]');
    if (total) total.textContent = pad(items.length);

    const step = () => {
      if (items.length < 2) return rail.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    };

    let frame = 0;
    const update = () => {
      frame = 0;
      const max = rail.scrollWidth - rail.clientWidth;
      const overflow = max > 2;
      const x = rail.scrollLeft;
      root.classList.toggle('has-more-left', overflow && x > 2);
      root.classList.toggle('has-more-right', overflow && x < max - 2);
      if (!controls) return;
      controls.hidden = !overflow;
      if (!overflow) return;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
      if (bar) {
        const visible = rail.clientWidth / rail.scrollWidth;
        bar.style.setProperty('--als-ugc-bar-w', `${Math.max(visible * 100, 8)}%`);
        bar.style.setProperty('--als-ugc-bar-x', `${(x / rail.clientWidth) * 100}%`);
      }
      if (current) {
        // Clips at least half in view: one number on a phone, a range on desktop.
        const inView = items
          .map((item, i) => ({ i, mid: item.offsetLeft + item.offsetWidth / 2 }))
          .filter(({ mid }) => mid >= x && mid <= x + rail.clientWidth)
          .map(({ i }) => i + 1);
        const first = inView[0] || 1;
        const last = inView[inView.length - 1] || first;
        current.textContent = first === last ? pad(first) : `${pad(first)}–${pad(last)}`;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    const page = (dir) => {
      const s = step();
      const perView = Math.max(1, Math.floor((rail.clientWidth + 1) / s));
      rail.scrollBy({ left: dir * perView * s, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };
    prev?.addEventListener('click', () => page(-1));
    next?.addEventListener('click', () => page(1));

    rail.addEventListener('scroll', schedule, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(rail);
    else window.addEventListener('resize', schedule, { passive: true });
    update();

    // Posters after the first four load only as they near the visible rail.
    const lazy = items.map((item) => item.querySelector('video[data-poster]')).filter(Boolean);
    if (!lazy.length) return;
    const fill = (video) => {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    };
    if (!('IntersectionObserver' in window)) {
      lazy.forEach(fill);
      return;
    }
    const posterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          fill(entry.target);
          posterObserver.unobserve(entry.target);
        });
      },
      { root: rail, rootMargin: '0px 120% 0px 120%' }
    );
    lazy.forEach((video) => posterObserver.observe(video));
  };

  const boot = () => {
    document.querySelectorAll('[data-ugc-rail-root]').forEach(setupRail);
    const videos = Array.from(document.querySelectorAll(VIDEO));
    if (!videos.length) return;

    videos.forEach((video) => {
      syncPlayState(video);
      syncMuteState(video);
    });

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) return;
          const video = entry.target;
          if (video instanceof HTMLVideoElement && !video.paused) {
            video.pause();
            syncPlayState(video);
          }
        });
      },
      { threshold: 0.35 }
    );
    videos.forEach((video) => observer.observe(video));
  };

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot, { once: true });
})();
